import {
  Address,
  LabelId,
  RecipientType,
  UserCommunication,
} from '@this+that/models';

import type { DraftVersionField } from './resolveLoadedDraftVersion';
import type { DraftData } from './useSmartDraftManager';
import { UserCommunicationFilter } from '../../../../../hooks/UserCommunications/filter/useFilterUserCommunications';
import { HandlerButton } from '../../../../../hooks/UserCommunications/useViewMessageComposerHandler';
import type { DraftRowPatch } from '../../../../../store/Inbox/UserCommunications/types';
import { handleSelectDraftReqType } from '../../../../../utils/handleSelectDraftReqType';
import { mergeRowAttachments } from '../../../../../utils/mergeRowAttachments';
import { threadHasNonDraftMessage } from '../../../../../utils/userCommunicationHelpers';

export interface DraftRecipient extends Address {
  type: RecipientType;
}

export interface CommitSavedDraftStoreActions {
  upsertDraft: (
    draftData: DraftRowPatch,
    messageId?: string,
    inboxEntryId?: string,
  ) => void;
  upsertDraftInInboxEntry: (
    inboxEntryId: string,
    draftCommunication: Partial<UserCommunication>,
    messageId?: string,
  ) => void;
}

export interface CommitSavedDraftInput {
  draftId: string;
  platform: UserCommunication['platform'];
  messageRawText: UserCommunication['messageRawText'];
  draftVersion?: string;
  pinnedAccountId?: string;
  currentAccountDisplayName?: string;
  draftData: DraftData;
  recipients: DraftRecipient[];
  handler?: HandlerButton;
  sourceUserCommunication?: UserCommunication;
  threadContext?: UserCommunication;
  isInboxEmail: boolean;
  isSentEmail: boolean;
  tabParam?: string;
  inboxEntryId?: string;
  actions: CommitSavedDraftStoreActions;
}

export function flattenUserCommunicationThreads(
  communication?: UserCommunication,
): UserCommunication[] {
  if (!communication) {
    return [];
  }

  const { threadMessages = [], ...mainCommunication } = communication;

  const all = [mainCommunication, ...threadMessages];

  const seen = new Set<string>();
  const unique = all.filter((msg) => {
    if (!msg.messageId) {
      return true;
    }
    if (seen.has(msg.messageId)) {
      return false;
    }
    seen.add(msg.messageId);
    return true;
  });

  return unique;
}

export const isThreadBoundHandler = (
  selectedHandlerButton: HandlerButton | undefined,
): boolean =>
  selectedHandlerButton === HandlerButton.REPLY ||
  selectedHandlerButton === HandlerButton.REPLY_ALL ||
  selectedHandlerButton === HandlerButton.FORWARD;

// The row carrying the thread the draft belongs to: the explicit carrier when
// the draft is nested in it, else the draft's own row once it is the head of a
// thread that still has a non-draft message.
const resolveThreadSource = (
  userCommunication: UserCommunication | undefined,
  threadContext: UserCommunication | undefined,
): UserCommunication | undefined => {
  if (threadContext) {
    return threadContext;
  }
  return userCommunication?.draftId &&
    threadHasNonDraftMessage(userCommunication)
    ? userCommunication
    : undefined;
};

// The replied-to row's own threadMessageId identifies the thread; a row with
// none yet (its first reply) falls back to its messageId so a later stage can
// still key a thread-head replacement off this field. A thread source decides
// the id whatever the handler is.
export const resolveDraftThreadMessageId = (
  selectedHandlerButton: HandlerButton | undefined,
  userCommunication: UserCommunication | undefined,
  threadContext?: UserCommunication,
): string | undefined => {
  const source = resolveThreadSource(userCommunication, threadContext);
  if (source) {
    return source.threadMessageId ?? source.messageId;
  }
  return isThreadBoundHandler(selectedHandlerButton)
    ? (userCommunication?.threadMessageId ?? userCommunication?.messageId)
    : undefined;
};

const resolveDraftCategory = (tabParam?: string): LabelId => {
  switch (tabParam) {
    case UserCommunicationFilter.PROMOTIONS:
      return LabelId.CATEGORY_PROMOTIONS;
    case UserCommunicationFilter.FORUMS:
      return LabelId.CATEGORY_FORUMS;
    case UserCommunicationFilter.SOCIAL:
      return LabelId.CATEGORY_SOCIAL;
    case UserCommunicationFilter.UPDATES:
      return LabelId.CATEGORY_UPDATES;
    default:
      return LabelId.CATEGORY_PERSONAL;
  }
};

export const buildDraftLabels = (
  isInboxEmail: boolean,
  isSentEmail: boolean,
  tabParam?: string,
): LabelId[] => {
  const labels = new Set<LabelId>();
  labels.add(LabelId.DRAFT);
  if (isInboxEmail) {
    labels.add(LabelId.INBOX);
    labels.add(resolveDraftCategory(tabParam));
  }
  if (isSentEmail) {
    labels.add(LabelId.SENT);
  }

  return Array.from(labels);
};

const isFreshForward = (
  handler: HandlerButton | undefined,
  source: UserCommunication | undefined,
): boolean =>
  handler === HandlerButton.FORWARD &&
  !!source &&
  !source.draftId &&
  !source.labelIds?.includes(LabelId.DRAFT);

const buildUserAttachments = (
  attachments: DraftData['attachments'],
): NonNullable<UserCommunication['attachments']> =>
  (attachments ?? []).map((a) => ({
    fileName: a.name,
    fileType: a.contentType,
    content: a.contentBytes,
    size: a.file.size,
  }));

const buildDraftAttachments = (
  draftData: DraftData,
  handler: HandlerButton | undefined,
  source: UserCommunication | undefined,
): UserCommunication['attachments'] => {
  const originals = isFreshForward(handler, source) ? source?.attachments : [];
  const merged = mergeRowAttachments(
    originals,
    buildUserAttachments(draftData.attachments),
  );
  return merged.length > 0 ? merged : undefined;
};

const buildDraftThreadMessages = (
  handler: HandlerButton | undefined,
  userCommunication: UserCommunication | undefined,
  threadContext: UserCommunication | undefined,
  draftId: string,
): UserCommunication[] => {
  const source = resolveThreadSource(userCommunication, threadContext);
  if (!source && !isThreadBoundHandler(handler)) {
    return [];
  }
  return flattenUserCommunicationThreads(source ?? userCommunication)
    .filter(
      (thread) =>
        thread.draftId !== draftId &&
        (!userCommunication?.draftId ||
          thread.messageId !== userCommunication.messageId),
    )
    .map((thread) => ({
      ...thread,
      attachments: undefined,
    }));
};

export function buildSavedDraft(
  input: Omit<CommitSavedDraftInput, 'actions'>,
): DraftRowPatch & DraftVersionField {
  const {
    draftData,
    handler,
    sourceUserCommunication,
    threadContext,
    draftId,
  } = input;
  return {
    draftId,
    messageId: draftId,
    ...(input.draftVersion ? { draftVersion: input.draftVersion } : {}),
    threadMessageId: resolveDraftThreadMessageId(
      handler,
      sourceUserCommunication,
      threadContext,
    ),
    draftType: handleSelectDraftReqType(handler),
    userEmailAddress:
      input.pinnedAccountId ||
      draftData.fromEmail ||
      input.currentAccountDisplayName,
    platform: input.platform,
    subject: draftData.subject,
    labelIds: buildDraftLabels(
      input.isInboxEmail,
      input.isSentEmail,
      input.tabParam,
    ),
    attachments: buildDraftAttachments(
      draftData,
      handler,
      sourceUserCommunication,
    ),
    attachmentsToRemove: draftData.attachmentsToRemove,
    messageText: draftData.message,
    messageRawText: input.messageRawText,
    threadMessages: buildDraftThreadMessages(
      handler,
      sourceUserCommunication,
      threadContext,
      draftId,
    ),
    messagePreview: draftData.message,
    recipients: input.recipients.map((recipient) => ({
      userId: recipient.value,
      email: recipient.value,
      name: recipient.label,
      type: recipient.type,
    })),
  };
}

export function commitSavedDraftToStores(
  input: CommitSavedDraftInput,
): Partial<UserCommunication> {
  const { actions, inboxEntryId, draftData } = input;
  const partialDraft = buildSavedDraft(input);
  const { attachmentsToRemove: _removed, ...rowDraft } = partialDraft;

  actions.upsertDraft(partialDraft, draftData.messageId, inboxEntryId);

  if (inboxEntryId) {
    actions.upsertDraftInInboxEntry(
      inboxEntryId,
      rowDraft as UserCommunication,
      draftData.messageId,
    );
  }

  return partialDraft;
}
